import { FiBook } from 'react-icons/fi';

export const placesDirectionsData = {
  courseId: 'places-directions',
  courseType: 'vocabulary',
  title: 'Places & Directions',
  icon: FiBook,
  overview: {
    impact: 'Navigate location descriptions in Listening and Speaking',
    focus: 'Places in a town, giving and understanding directions',
    uses: 'Listening Section 1, Speaking descriptions',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'Places in Town',
      content: `<p>Common locations you'll hear in IELTS Listening:</p>`,
      points: [
        'Services: post office, bank, library, police station, hospital, pharmacy',
        'Shopping: department store, shopping mall/centre, market, bakery',
        'Leisure: cinema, museum, gallery, park, sports centre, swimming pool',
        'Transport: bus stop, train station, car park, airport, taxi rank'
      ]
    },
    {
      title: 'Giving Directions',
      content: `<p>Key phrases for describing location and movement:</p>`,
      points: [
        'Go straight ahead / Go along this road',
        'Turn left/right at the traffic lights/junction/roundabout',
        'It\'s on the left/right / It\'s opposite the bank',
        'It\'s next to / beside / between / behind / in front of',
        'It\'s on the corner of Main Street and Park Road',
        'Take the first/second turning on the left'
      ]
    }
  ],
  exercises: [
    {
      title: 'Follow the Directions',
      type: 'select',
      instructions: 'Choose the correct answer based on the direction.',
      questions: [
        {
          text: '\'Go straight, then turn right. It\'s on your left.\' Where is it?',
          options: [
            'On the left side of the road after turning right',
            'On the right after going straight',
            'Straight ahead'
          ],
          answer: 'On the left side of the road after turning right',
          explanation: 'After turning right, look to your left.'
        },
        {
          text: 'The bank is ___ the post office.',
          options: [
            'opposite',
            'between',
            'along'
          ],
          answer: 'opposite',
          explanation: '\'Opposite\' means directly across from.'
        }
      ]
    },
    {
      title: 'Match Place to Description',
      type: 'select',
      instructions: 'Choose the place that matches each description.',
      questions: [
        {
          text: 'You go here when you are ill and need to see a doctor.',
          options: ['hospital', 'pharmacy', 'library', 'post office'],
          answer: 'hospital',
          explanation: 'A hospital is where you receive medical treatment from doctors and nurses.'
        },
        {
          text: 'You go here to borrow books for free.',
          options: ['library', 'bookshop', 'museum', 'gallery'],
          answer: 'library',
          explanation: 'A library lends books for free. A bookshop sells them.'
        },
        {
          text: 'You go here to send a letter or a parcel.',
          options: ['post office', 'bank', 'police station', 'department store'],
          answer: 'post office',
          explanation: 'A post office handles mail, parcels, and sometimes other services.'
        },
        {
          text: 'You go here to deposit or withdraw money.',
          options: ['bank', 'market', 'post office', 'shopping mall'],
          answer: 'bank',
          explanation: 'A bank is a financial institution where you manage your money.'
        }
      ]
    },
    {
      title: 'Complete Directions',
      type: 'fill-in',
      instructions: 'Type the correct word or phrase to complete each direction.',
      questions: [
        {
          text: 'Go straight ahead, then turn ___ at the traffic lights.',
          answer: 'left',
          explanation: '\'Turn left\' and \'turn right\' are the most basic direction phrases.'
        },
        {
          text: 'The cinema is ___ the supermarket — directly across the road.',
          answer: 'opposite',
          explanation: '\'Opposite\' means on the other side, facing the referenced place.'
        },
        {
          text: 'Walk along this road and take the second ___ on the right.',
          answer: 'turning',
          explanation: '\'Take the first/second turning\' is a common way to give directions in British English.'
        },
        {
          text: 'The pharmacy is ___ the bank and the post office.',
          answer: 'between',
          explanation: '\'Between\' means in the middle of two places: \'between A and B\'.'
        }
      ]
    },
    {
      title: 'Give Directions',
      type: 'writing',
      instructions: 'Write 1-2 sentences giving directions as described. Use direction vocabulary from the lesson.',
      questions: [
        {
          text: 'Give directions from a bus stop to a nearby hospital.',
          answer: 'go straight, turn, on your left',
          explanation: 'Example: \'Go straight ahead from the bus stop and turn right at the roundabout. The hospital is on your left, next to the pharmacy.\' Use sequencing and prepositions of place.'
        },
        {
          text: 'Describe where the library is in relation to other buildings.',
          answer: 'opposite, next to, between',
          explanation: 'Example: \'The library is between the post office and the bank, opposite the park.\' Use at least two prepositions of place.'
        },
        {
          text: 'Tell someone how to walk from a train station to a shopping mall.',
          answer: 'turn left, go along, on your right',
          explanation: 'Example: \'Come out of the train station and turn left. Go along Main Street for about five minutes and the shopping mall is on your right.\' Include distance or time estimates for natural-sounding directions.'
        }
      ]
    }
  ],
  tips: [
    'Learn prepositions of place: next to, opposite, between, behind',
    'Practice describing how to get to places from your home',
    'Directions vocabulary is high-frequency in Listening Section 1',
    'Learn landmarks: traffic lights, roundabout, junction, crossing'
  ],
  dailyPractice: [
    'Practice places & directions concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Places & Directions: Town locations and direction-giving vocabulary. Key for IELTS Listening Section 1 map/direction questions.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...placesDirectionsData,
      title: placesDirectionsData.title + ' (Foundations)',
      overview: { ...placesDirectionsData.overview, duration: '15-20 minutes' },
      sections: placesDirectionsData.sections.slice(0, Math.max(2, Math.ceil(placesDirectionsData.sections.length / 2))),
      exercises: placesDirectionsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...placesDirectionsData,
      title: placesDirectionsData.title + ' (Advanced)',
      overview: { ...placesDirectionsData.overview, duration: '30-35 minutes' },
      sections: placesDirectionsData.sections,
      exercises: placesDirectionsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(placesDirectionsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return placesDirectionsData;
}
